import { useState, type FormEvent } from "react";
import { apiFetch } from "../api";
import { useT } from "../i18n";
import { Button } from "./Button";
import { Field, Input } from "./Field";
import { ErrorBox } from "./Misc";

/** sessionStorage key: the e-mail typed on the start page, picked up once by the tenant's login page. */
export const LOGIN_HINT_KEY = "iga_login_hint";

/** The e-mail the start page handed over to the login page (empty when there is none). */
export function readLoginHint(): string {
  try { return sessionStorage.getItem(LOGIN_HINT_KEY) ?? ""; } catch { return ""; }
}

export function clearLoginHint(): void {
  try { sessionStorage.removeItem(LOGIN_HINT_KEY); } catch { /* storage blocked */ }
}

interface Match { slug: string; displayName: string; entra?: boolean }

/**
 * Shown when the platform is opened on the base domain or an IP address (no tenant in the host name).
 * The user types their work e-mail and the organisation is found from its verified e-mail domain
 * (home realm discovery); the organisation's short name is the fallback. The choice is stored in a
 * cookie by the API, and the page then reloads into that tenant's login with the e-mail filled in.
 */
export function TenantChooser({ productName = "IGA Platform" }: { productName?: string }) {
  const t = useT();
  const [mode, setMode] = useState<"email" | "pick" | "manual">("email");
  const [email, setEmail] = useState("");
  const [slug, setSlug] = useState("");
  const [matches, setMatches] = useState<Match[]>([]);
  const [notFound, setNotFound] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function enter(target: string, hint?: string, entra?: boolean) {
    await apiFetch("/api/public/select-tenant", { method: "POST", json: { slug: target } });
    if (entra && hint) {
      // The organisation signs in with Microsoft Entra ID: go straight there with the address filled in.
      const q = new URLSearchParams({ login_hint: hint, domain_hint: hint.slice(hint.lastIndexOf("@") + 1), returnTo: window.location.pathname + window.location.search });
      window.location.href = `/api/auth/entra/start?${q}`;
      return;
    }
    if (hint) { try { sessionStorage.setItem(LOGIN_HINT_KEY, hint); } catch { /* storage blocked: the user types it again */ } }
    window.location.reload();
  }

  async function discover(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setNotFound(null);
    try {
      const { items } = await apiFetch<{ items: Match[] }>("/api/public/discover", { method: "POST", json: { email } });
      if (items.length === 1) await enter(items[0]!.slug, email, items[0]!.entra);
      else if (items.length > 1) { setMatches(items); setMode("pick"); }
      else { setNotFound(email.slice(email.lastIndexOf("@") + 1)); setMode("manual"); }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(false); }
  }

  async function chooseManual(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await enter(slug, email || undefined); }
    catch (err) { setError(err instanceof Error ? err.message : t("ui.chooser.unknownOrganisation")); }
    finally { setBusy(false); }
  }

  async function hosting() {
    await apiFetch("/api/public/select-tenant", { method: "POST", json: { slug: "__hosting__" } });
    window.location.href = "/";
  }

  function backToEmail() { setMode("email"); setNotFound(null); setError(null); }

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="flex w-full max-w-sm flex-col gap-4 rounded-dialog bg-surface p-7 shadow-e3">
        <h1 className="text-[20px] font-bold leading-7">{productName}</h1>
        {mode === "email" && (
          <>
            <p className="text-fg-2">{t("ui.chooser.intro")}</p>
            <form onSubmit={discover} className="flex flex-col gap-3">
              <Field label={t("ui.chooser.email")}><Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" autoFocus required /></Field>
              <ErrorBox error={error} />
              <Button type="submit" size="touch" loading={busy}>{t("ui.chooser.continue")}</Button>
            </form>
            <button type="button" className="self-start text-small font-semibold text-brand-text hover:underline" onClick={() => { setError(null); setMode("manual"); }}>{t("ui.chooser.useManual")}</button>
          </>
        )}
        {mode === "pick" && (
          <>
            <p className="text-fg-2">{t("ui.chooser.pickIntro", { email })}</p>
            <div className="flex flex-col gap-2">
              {matches.map((m) => (
                <Button key={m.slug} variant="secondary" size="touch" disabled={busy} onClick={() => { setBusy(true); enter(m.slug, email, m.entra).catch((err) => { setError(err instanceof Error ? err.message : String(err)); setBusy(false); }); }}>
                  <span className="flex w-full items-center justify-between gap-2"><span>{m.displayName}</span><span className="text-small text-fg-3">{m.slug}</span></span>
                </Button>
              ))}
            </div>
            <ErrorBox error={error} />
            <button type="button" className="self-start text-small font-semibold text-brand-text hover:underline" onClick={backToEmail}>{t("ui.chooser.back")}</button>
          </>
        )}
        {mode === "manual" && (
          <>
            <p className="text-fg-2">{notFound ? t("ui.chooser.notFound", { domain: notFound }) : t("ui.chooser.manualIntro")}</p>
            <form onSubmit={chooseManual} className="flex flex-col gap-3">
              <Field label={t("ui.chooser.organisation")}><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="e.g. acme" autoFocus required /></Field>
              <ErrorBox error={error} />
              <Button type="submit" size="touch" loading={busy}>{t("ui.chooser.continue")}</Button>
            </form>
            <button type="button" className="self-start text-small font-semibold text-brand-text hover:underline" onClick={backToEmail}>{notFound ? t("ui.chooser.back") : t("ui.chooser.useEmail")}</button>
          </>
        )}
        <div className="flex justify-between border-t border-line pt-3 text-small">
          <a className="font-semibold hover:underline" href="/signup">{t("ui.chooser.signup")}</a>
          <button type="button" className="font-semibold text-brand-text hover:underline" onClick={hosting}>{t("ui.chooser.hosting")}</button>
        </div>
      </div>
    </div>
  );
}
